import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";

// Realtime de pedidos via WebSocket (campainha do StoreAgentDO no Worker),
// substituindo o polling de 15s que estourou o egress do Supabase (jun/2026).
// Fluxo:
//   1. busca token curto autenticado na API (GET /api/orders/ws-token)
//   2. conecta no Worker — wss://<origin do SPA>/api/orders/ws?token=...
//      (o WS vive no Worker, mesma origin do painel; NÃO em api.*)
//   3. ao receber {type:"new_order"}, invalida a query de pedidos → o
//      use-new-order-sound, o use-auto-print-watcher e a tela de Pedidos
//      refazem o fetch (da janela recente) e reagem.
//
// Backstop de 60s: única rede de segurança pra evento perdido durante
// desconexão (sem voltar pro polling de 15s). Reconecta com backoff.
// Montado uma vez no AdminLayout.

const BACKSTOP_MS = 60_000;
const RECONNECT_MIN_MS = 2_000;
const RECONNECT_MAX_MS = 30_000;
const PING_MS = 25_000;

export function useOrdersRealtime(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    let ws: WebSocket | null = null;
    let stopped = false;
    let reconnectMs = RECONNECT_MIN_MS;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let pingTimer: ReturnType<typeof setInterval> | null = null;

    // Prefixo da queryKey gerada (getListOrdersQueryKey = ["/api/orders", ...]):
    // pega todas as variantes de params (sound/watcher usam {dateFrom}, a tela
    // de Pedidos usa sem params).
    const invalidateOrders = () => {
      void queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
    };

    const clearPing = () => {
      if (pingTimer) {
        clearInterval(pingTimer);
        pingTimer = null;
      }
    };

    const scheduleReconnect = () => {
      if (stopped || reconnectTimer) return;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        reconnectMs = Math.min(reconnectMs * 2, RECONNECT_MAX_MS);
        void connect();
      }, reconnectMs);
    };

    const connect = async () => {
      if (stopped) return;

      let token: string | null = null;
      try {
        const r = await customFetch<{ token: string }>("/api/orders/ws-token");
        token = r?.token ?? null;
      } catch {
        scheduleReconnect();
        return;
      }
      if (stopped || !token) {
        scheduleReconnect();
        return;
      }

      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const url = `${proto}://${window.location.host}/api/orders/ws?token=${encodeURIComponent(token)}`;

      try {
        ws = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }

      ws.onopen = () => {
        reconnectMs = RECONNECT_MIN_MS; // reset do backoff
        invalidateOrders(); // revalida ao (re)conectar — cobre gap de desconexão
        clearPing();
        pingTimer = setInterval(() => {
          try {
            ws?.send("ping");
          } catch {
            /* noop */
          }
        }, PING_MS);
      };

      ws.onmessage = (ev) => {
        let type: string | undefined;
        try {
          type = JSON.parse(ev.data as string)?.type;
        } catch {
          return;
        }
        if (type === "new_order") invalidateOrders();
      };

      ws.onclose = () => {
        clearPing();
        scheduleReconnect();
      };
      ws.onerror = () => {
        // onerror precede onclose; só fecha (o onclose agenda o reconnect).
        try {
          ws?.close();
        } catch {
          /* noop */
        }
      };
    };

    void connect();

    // Backstop de 60s: única rede de segurança pra evento perdido.
    const backstop = setInterval(invalidateOrders, BACKSTOP_MS);

    return () => {
      stopped = true;
      clearInterval(backstop);
      clearPing();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try {
        ws?.close();
      } catch {
        /* noop */
      }
    };
  }, [queryClient]);
}
